/**
 * masterDataCache — 마스터 데이터 메모리 캐시
 *
 * V259: 조직/직위/직책 등 마스터 데이터의 반복 호출을 줄이기 위한
 * module-level 메모리 캐시. TTL 기반으로 동작하며,
 * 데이터 수정 시 invalidate 함수를 호출하여 즉시 무효화.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5분

const cache = new Map<string, CacheEntry<any>>();

/**
 * 캐시에서 데이터를 가져온다. TTL이 만료되었으면 null을 반환.
 */
export function getCached<T>(key: string, ttl: number = DEFAULT_TTL): T | null {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttl) {
    cache.delete(key);
    return null;
  }
  return entry.data as T;
}

/**
 * 캐시에 데이터를 저장한다.
 */
export function setCached<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

/**
 * 특정 prefix로 시작하는 모든 캐시를 무효화한다.
 */
export function invalidateCache(prefix: string): void {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.startsWith(prefix)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => cache.delete(key));
}

// 캐시 키 prefix 상수
export const CACHE_KEY = {
  ORG_UNITS: 'orgUnits:',
  ORG_UNITS_PUBLIC: 'orgUnitsPublic:',
  POSITION_LEVELS: 'positionLevels:',
  JOB_TITLES: 'jobTitles:',
} as const;
